// Messages Page
// Main messaging interface for caregivers, therapists, and parents

import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  Fade,
  SwipeableDrawer
} from '@mui/material';

// Hooks and Services
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../services/firebase';
import { useChildContext } from '../contexts/ChildContext';
import { useRole } from '../contexts/RoleContext';
import useMessagesPageState from '../hooks/messaging/useMessagesPageState';
import { sendMessage } from '../services/messaging';
import colors from '../assets/theme/colors';

// Components
import ConversationList from '../components/Messaging/ConversationList';
import MessageThread from '../components/Messaging/MessageThread';
import NewConversationModal from '../components/Messaging/NewConversationModal';
import ChildContextHeader from '../components/Messaging/ChildContextHeader';
import ChatNotificationPrompt from '../components/Messaging/ChatNotificationPrompt';
import QuickCheckIn from '../components/Mobile/QuickCheckIn';
import BulkMedicationLogDialog from './MedicalLog/components/BulkMedicationLogDialog';
import { TERMINOLOGY } from '../constants/terminology';

/**
 * Main messaging page with responsive layout
 * Desktop: Split view (conversations | messages)  
 * Mobile: Stack navigation
 */
const MessagesPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [user] = useAuthState(auth);
  const { currentChildId } = useChildContext();
  const { childrenWithAccess = [] } = useRole();
  const searchParams = new URLSearchParams(location.search);
  const searchChildId = searchParams.get('childId');
  const searchConversationId = searchParams.get('conversationId');
  const searchOpenChildChat = searchParams.get('openChildChat') === '1';
  const searchReturnToDashboard = searchParams.get('returnToDashboard') === '1';
  const openChildChat = Boolean(location.state?.openChildChat || searchOpenChildChat);
  const _returnToDashboard = Boolean(location.state?.returnToDashboard || location.state?.selectedChildId || searchReturnToDashboard || searchChildId);

  const selectedChild = childrenWithAccess?.find((child) => child.id === currentChildId) || null;

  // Get child context from navigation state or use selected child
  const contextChildId = location.state?.selectedChildId || searchChildId || selectedChild?.id || currentChildId;
  const contextChild = childrenWithAccess?.find(child => child.id === contextChildId);
  const [showQuickUpdate, setShowQuickUpdate] = useState(false);
  const [showMedicationDialog, setShowMedicationDialog] = useState(false);
  const [threadRefreshKey, setThreadRefreshKey] = useState(0);

  const {
    availableContacts,
    canStartConversation,
    conversations,
    contextChildRole,
    contactsByChildId,
    handleConversationCreated,
    handleDeactivateConversation,
    handleConversationSelect,
    handleModeChange,
    hasOpenedChildChat,
    loadConversations,
    loading,
    messagingMode,
    mobileView,
    manageableChildren,
    openingChildChat,
    resetToConversationList,
    selectedConversation,
    setShowNewConversation,
    showNewConversation,
  } = useMessagesPageState({
    userId: user?.uid,
    isMobile,
    selectedChildId: selectedChild?.id,
    selectedChild,
    childrenWithAccess,
    contextChildId,
    contextChild,
    openChildChat,
    initialSelectedConversationId: location.state?.selectedConversationId || searchConversationId || null,
    initialMobileView: openChildChat || searchConversationId ? 'thread' : 'conversations',
  });

  const activeChild = contextChild || selectedChild || null;
  const activeConversationId = selectedConversation?.id || null;
  const refreshConversations = useCallback(
    () => loadConversations({ background: true }),
    [loadConversations]
  );

  const senderName = useMemo(
    () => user?.displayName || user?.email?.split('@')[0] || 'Care Circle Member',
    [user?.displayName, user?.email]
  );

  const postConversationSummary = async (text) => {
    if (!activeConversationId || !user?.uid || !text?.trim()) {
      return;
    }

    const result = await sendMessage({
      conversationId: activeConversationId,
      senderId: user.uid,
      senderName,
      text: text.trim(),
      metadata: {
        childId: contextChildId || activeChild?.id || null,
      },
    });

    if (result.success) {
      refreshConversations();
      setThreadRefreshKey((current) => current + 1);
    }
  };

  const handleOpenCareTeamDetails = () => {
    if (!contextChildId && !activeChild?.id) {
      return;
    }

    navigate('/care-team', {
      state: {
        focusChildId: contextChildId || activeChild?.id,
      },
    });
  };

  const handleQuickUpdateComplete = async (payload) => {
    setShowQuickUpdate(false);

    const structuredCount = Array.isArray(payload?.structuredEntries) ? payload.structuredEntries.length : 0;
    const summaryText = payload?.text?.trim()
      ? `Update shared: ${payload.text.trim()}`
      : structuredCount > 1
        ? `Update shared: ${structuredCount} new updates were added.`
        : 'Update shared: A new update was added.';

    await postConversationSummary(summaryText);
  };

  const handleMedicationSaved = async (payload) => {
    setShowMedicationDialog(false);

    const summaryText = payload?.medicationName
      ? `Medication logged: ${payload.medicationName}${payload.time ? ` given at ${payload.time}.` : '.'}`
      : 'Medication logged for the care circle.';

    await postConversationSummary(summaryText);
  };

  const renderEmptyChildChatState = () => (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'text.secondary',
        gap: 2,
        px: 3,
        py: 8,
        textAlign: 'center',
      }}
    >
      <Box sx={{ fontSize: '4rem', opacity: 0.3 }}>💬</Box>
      <Typography variant="h6" sx={{ opacity: 0.85, fontWeight: 700 }}>
        No care circle chat yet
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.7, maxWidth: 420 }}>
        {canStartConversation
          ? `This ${TERMINOLOGY.subjectLower} chat opens when at least one other approved care circle member is available. If no caregivers have been added yet, you’ll see an empty conversation area here.`
          : `You can participate in this ${TERMINOLOGY.subjectLower}'s care circle chat, but only the Care Owner or Care Partner can start it.`}
      </Typography>
      {canStartConversation && (
        <Button
          variant="contained"
          onClick={() => setShowNewConversation(true)}
          sx={{
            mt: 1,
            borderRadius: 999,
            px: 3,
            py: 1.1,
            textTransform: 'none',
            fontWeight: 700,
          }}
          aria-label={`Start ${TERMINOLOGY.subjectLower} care circle chat`}
        >
          Start chat
        </Button>
      )}
    </Box>
  );

  const handlePageBack = () => {
    if (isMobile && mobileView === 'thread') {
      resetToConversationList();
      return;
    }

    if (_returnToDashboard || contextChildId) {
      navigate(
        {
          pathname: '/dashboard',
          search: '',
        },
        {
          replace: true,
          state: {},
        }
      );
      return;
    }

    navigate(-1);
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Fade in={true}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '60vh',
              gap: 3,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress
                size={60}
                thickness={4}
                sx={{
                  color: 'primary.main',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  },
                }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  fontSize: '2rem',
                  animation: 'pulse 2s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': {
                      opacity: 0.7,
                      transform: 'scale(1)',
                    },
                    '50%': {
                      opacity: 1,
                      transform: 'scale(1.1)',
                    },
                  },
                }}
              >
                💬
              </Box>
            </Box>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                textAlign: 'center',
                fontFamily: '"Lancelot", serif',
              }}
            >
              Loading your messages...
            </Typography>
          </Box>
        </Fade>
      </Container>
    );
  }

  // Mobile layout
  if (isMobile) {
    return (
      <Box
        sx={{
          mt: 1,
          mx: { xs: -1.5, sm: 0 },
          minHeight: 'calc(100dvh - 112px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {openingChildChat && !selectedConversation ? (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 2,
              color: 'text.secondary',
            }}
          >
            <CircularProgress size={36} />
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {`Opening ${TERMINOLOGY.subjectLower} chat...`}
            </Typography>
          </Box>
        ) : mobileView === 'conversations' ? (
          // Mobile conversations view
          <Box>
            <ChildContextHeader
              contextChild={contextChild}
              messagingMode={messagingMode}
              onModeChange={handleModeChange}
              careTeamCount={messagingMode === 'child_specific' ? availableContacts.length : 0}
              allContactsCount={availableContacts.length}
              isMobile={true}
              onBack={handlePageBack}
              contextChildRole={contextChildRole}
              canStartConversation={canStartConversation}
            />
            <ChatNotificationPrompt />
            
            <ConversationList
              conversations={conversations}
              selectedConversation={selectedConversation}
              onConversationSelect={handleConversationSelect}
              onNewConversation={canStartConversation ? () => setShowNewConversation(true) : null}
              onDeactivateConversation={handleDeactivateConversation}
              currentUserId={user?.uid}
              isMobile={true}
              canStartConversation={canStartConversation}
              children={childrenWithAccess}
            />
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 'calc(100dvh - 124px)',
              background: 'linear-gradient(180deg, #FCFFFE 0%, #F6FBFA 100%)',
            }}
          >
            {selectedConversation ? (
              <MessageThread
                conversation={selectedConversation}
                currentUserId={user?.uid}
                onConversationUpdate={refreshConversations}
                isMobile={true}
                contextChild={activeChild}
                contextChildRole={contextChildRole}
                onBack={handlePageBack}
                onOpenDetails={handleOpenCareTeamDetails}
                onOpenMedication={() => setShowMedicationDialog(true)}
                onOpenUpdate={() => setShowQuickUpdate(true)}
                refreshKey={threadRefreshKey}
              />
            ) : (
              renderEmptyChildChatState()
            )}
          </Box>
        )}

        <NewConversationModal
          open={showNewConversation}
          onClose={() => setShowNewConversation(false)}
          onConversationCreated={handleConversationCreated}
          currentUserId={user?.uid}
          selectedChildId={canStartConversation ? contextChildId : null}
          children={manageableChildren}
          availableContacts={availableContacts}
          contactsByChildId={contactsByChildId}
        />

        <SwipeableDrawer
          anchor="bottom"
          open={showQuickUpdate}
          onOpen={() => {}}
          onClose={() => setShowQuickUpdate(false)}
          disableBackdropTransition
          disableDiscovery
          PaperProps={{
            sx: {
              borderRadius: '24px 24px 0 0',
              bgcolor: colors.landing.surface,
              borderTop: `1px solid ${colors.landing.borderLight}`,
              boxShadow: `0 -18px 48px ${colors.landing.shadowPanel}`,
              maxHeight: '92vh',
              pb: 'env(safe-area-inset-bottom)',
              overflow: 'hidden',
            },
          }}
        >
          <Box sx={{ px: 1.25, pt: 0.75, pb: 1.25, overflow: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 4,
                  borderRadius: 9999,
                  bgcolor: colors.landing.borderLight,
                }}
              />
            </Box>
            {activeChild ? (
              <QuickCheckIn
                child={activeChild}
                onComplete={handleQuickUpdateComplete}
                onSkip={() => setShowQuickUpdate(false)}
                open={showQuickUpdate}
              />
            ) : null}
          </Box>
        </SwipeableDrawer>

        <BulkMedicationLogDialog
          open={showMedicationDialog}
          childId={activeChild?.id || contextChildId}
          childName={activeChild?.name || ''}
          child={activeChild}
          onClose={() => setShowMedicationDialog(false)}
          onSaved={handleMedicationSaved}
        />
      </Box>
    );
  }

  // Desktop layout
  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <ChildContextHeader
        contextChild={contextChild}
        messagingMode={messagingMode}
        onModeChange={handleModeChange}
        careTeamCount={messagingMode === 'child_specific' ? availableContacts.length : 0}
        allContactsCount={availableContacts.length}
        isMobile={false}
        contextChildRole={contextChildRole}
        canStartConversation={canStartConversation}
      />
      <ChatNotificationPrompt />

      <Paper
        elevation={2}
        sx={{
          display: 'flex',
          height: '75vh',
          overflow: 'hidden',
          borderRadius: 2,
        }}
      >
        {/* Left Panel: Conversations */}
        <Box
          sx={{
            width: 380,
            borderRight: 1,
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <ConversationList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onConversationSelect={handleConversationSelect}
            onNewConversation={canStartConversation ? () => setShowNewConversation(true) : null}
            onDeactivateConversation={handleDeactivateConversation}
            currentUserId={user?.uid}
            isMobile={false}
            canStartConversation={canStartConversation}
            children={childrenWithAccess}
          />
        </Box>

        {/* Right Panel: Message Thread */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {openingChildChat && !selectedConversation ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
                color: 'text.secondary',
              }}
            >
              <CircularProgress size={36} />
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {`Opening ${TERMINOLOGY.subjectLower} chat...`}
              </Typography>
            </Box>
          ) : selectedConversation ? (
            <MessageThread
              conversation={selectedConversation}
              currentUserId={user?.uid}
              onConversationUpdate={refreshConversations}
              isMobile={false}
              contextChild={contextChild}
              contextChildRole={contextChildRole}
            />
          ) : openChildChat && hasOpenedChildChat ? (
            renderEmptyChildChatState()
          ) : (
            // Empty state
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'text.secondary',
                gap: 2
              }}
            >
              <Box sx={{ fontSize: '4rem', opacity: 0.3 }}>💬</Box>
              <Typography variant="h6" sx={{ opacity: 0.7 }}>
                Select a conversation to start messaging
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5, textAlign: 'center', maxWidth: 300 }}>
                Choose from your existing conversations or create a new one to communicate with your care circle.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* New Conversation Modal */}
      <NewConversationModal
        open={showNewConversation}
        onClose={() => setShowNewConversation(false)}
        onConversationCreated={handleConversationCreated}
        currentUserId={user?.uid}
        selectedChildId={canStartConversation ? contextChildId : null}
        children={manageableChildren}
        availableContacts={availableContacts}
        contactsByChildId={contactsByChildId}
      />
    </Container>
  );
};

export default MessagesPage;
